// src/admin/UserRoleManagementPage.jsx
import React from "react";
import {
  Search,
  UserPlus,
  Edit2,
  Eye,
  UserMinus,
  X,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Shield,
  User,
  Mail,
  Phone,
  Calendar,
  Lock,
  RefreshCw,
  Download,
  Filter,
  ChevronDown,
  Copy,
  EyeOff,
  Unlock,
  Check,
} from "lucide-react";
import { TableSkeleton } from "../../components/ui/Skeleton.jsx";

/**
 * Enhanced User Role Management for Dynesourcer:
 * ✅ User Management (Add, Edit, View, Suspend/Activate, Remove)
 * ✅ Role Management (Admin, Manager, Staff with different permissions)
 * ✅ Permission Matrix (Visual permissions per role)
 * ✅ Audit Log (Track all user actions)
 * ✅ Password Management (Generate, Reset)
 * ✅ Search & Filter
 * ✅ Export functionality
 */

const initialUsers = [
  {
    id: "USR-001",
    name: "John Doe",
    email: "john.doe@dynesourcer.com",
    phone: "+44 20 1234 5678",
    username: "johndoe",
    role: "Admin",
    status: "Active",
    tone: "blue",
    createdDate: "2023-01-15",
    lastLogin: "2024-12-18 14:30:00",
    createdBy: "System",
    department: "Operations",
    notes: "Super admin with full access",
  },
  {
    id: "USR-002",
    name: "Jane Smith",
    email: "jane.smith@dynesourcer.com",
    phone: "+44 20 2345 6789",
    username: "janesmith",
    role: "Manager",
    status: "Suspended",
    tone: "red",
    createdDate: "2023-03-22",
    lastLogin: "2024-11-20 09:15:00",
    createdBy: "John Doe",
    department: "Sales",
    notes: "Suspended pending investigation",
  },
  {
    id: "USR-003",
    name: "Emile Turner",
    email: "emile.turner@dynesourcer.com",
    phone: "+44 20 3456 7890",
    username: "emileturner",
    role: "Staff",
    status: "Active",
    tone: "blue",
    createdDate: "2024-06-10",
    lastLogin: "2024-12-17 16:45:00",
    createdBy: "Jane Smith",
    department: "Support",
    notes: "Customer support specialist",
  },
  {
    id: "USR-004",
    name: "Michael Brown",
    email: "michael.brown@dynesourcer.com",
    phone: "+44 20 4567 8901",
    username: "michaelbrown",
    role: "Manager",
    status: "Active",
    tone: "blue",
    createdDate: "2023-08-05",
    lastLogin: "2024-12-18 11:20:00",
    createdBy: "John Doe",
    department: "Operations",
    notes: "Operations manager",
  },
  {
    id: "USR-005",
    name: "Sarah Williams",
    email: "sarah.williams@dynesourcer.com",
    phone: "+44 20 5678 9012",
    username: "sarahwilliams",
    role: "Staff",
    status: "Active",
    tone: "blue",
    createdDate: "2024-02-14",
    lastLogin: "2024-12-18 13:10:00",
    createdBy: "Michael Brown",
    department: "Support",
    notes: "Technical support staff",
  },
];

const rolePermissions = {
  Admin: {
    // User Management
    viewUsers: true,
    addUsers: true,
    editUsers: true,
    suspendUsers: true,
    removeUsers: true,
    // Borrower Management
    viewBorrowers: true,
    editBorrowers: true,
    blockBorrowers: true,
    // Lender Management
    viewLenders: true,
    addLenders: true,
    editLenders: true,
    removeLenders: true,
    // Application Management
    viewApplications: true,
    approveApplications: true,
    rejectApplications: true,
    // System
    viewAuditLog: true,
    manageRoles: true,
    exportData: true,
    systemSettings: true,
  },
  Manager: {
    // User Management
    viewUsers: true,
    addUsers: true,
    editUsers: true,
    suspendUsers: false,
    removeUsers: false,
    // Borrower Management
    viewBorrowers: true,
    editBorrowers: true,
    blockBorrowers: true,
    // Lender Management
    viewLenders: true,
    addLenders: true,
    editLenders: true,
    removeLenders: false,
    // Application Management
    viewApplications: true,
    approveApplications: true,
    rejectApplications: true,
    // System
    viewAuditLog: true,
    manageRoles: false,
    exportData: true,
    systemSettings: false,
  },
  Staff: {
    // User Management
    viewUsers: true,
    addUsers: false,
    editUsers: false,
    suspendUsers: false,
    removeUsers: false,
    // Borrower Management
    viewBorrowers: true,
    editBorrowers: false,
    blockBorrowers: false,
    // Lender Management
    viewLenders: true,
    addLenders: false,
    editLenders: false,
    removeLenders: false,
    // Application Management
    viewApplications: true,
    approveApplications: false,
    rejectApplications: false,
    // System
    viewAuditLog: false,
    manageRoles: false,
    exportData: false,
    systemSettings: false,
  },
};

const initialAuditLog = [
  {
    id: "LOG-001",
    user: "John Doe",
    userId: "USR-001",
    action: "Added User",
    details: "Added new user: Sarah Williams (Staff)",
    timestamp: "2024-12-18 10:00:00",
    ipAddress: "192.168.1.100",
  },
  {
    id: "LOG-002",
    user: "Jane Smith",
    userId: "USR-002",
    action: "Changed Role",
    details: "Changed Emile Turner role from Staff to Manager",
    timestamp: "2024-12-17 14:30:00",
    ipAddress: "192.168.1.101",
  },
  {
    id: "LOG-003",
    user: "John Doe",
    userId: "USR-001",
    action: "Suspended User",
    details: "Suspended Jane Smith account",
    timestamp: "2024-12-16 09:15:00",
    ipAddress: "192.168.1.100",
  },
  {
    id: "LOG-004",
    user: "Michael Brown",
    userId: "USR-004",
    action: "Reset Password",
    details: "Reset password for Emile Turner",
    timestamp: "2024-12-15 16:45:00",
    ipAddress: "192.168.1.102",
  },
  {
    id: "LOG-005",
    user: "John Doe",
    userId: "USR-001",
    action: "Updated Permissions",
    details: "Updated Manager role permissions",
    timestamp: "2024-12-14 11:20:00",
    ipAddress: "192.168.1.100",
  },
];

const roles = ["Admin", "Manager", "Staff"];
const departments = ["Operations", "Sales", "Support", "Finance", "IT", "HR"];

const permissionCategories = {
  "User Management": ["viewUsers", "addUsers", "editUsers", "suspendUsers", "removeUsers"],
  "Borrower Management": ["viewBorrowers", "editBorrowers", "blockBorrowers"],
  "Lender Management": ["viewLenders", "addLenders", "editLenders", "removeLenders"],
  "Application Management": ["viewApplications", "approveApplications", "rejectApplications"],
  "System": ["viewAuditLog", "manageRoles", "exportData", "systemSettings"],
};

const permissionLabels = {
  viewUsers: "View Users",
  addUsers: "Add Users",
  editUsers: "Edit Users",
  suspendUsers: "Suspend Users",
  removeUsers: "Remove Users",
  viewBorrowers: "View Borrowers",
  editBorrowers: "Edit Borrowers",
  blockBorrowers: "Block Borrowers",
  viewLenders: "View Lenders",
  addLenders: "Add Lenders",
  editLenders: "Edit Lenders",
  removeLenders: "Remove Lenders",
  viewApplications: "View Applications",
  approveApplications: "Approve Applications",
  rejectApplications: "Reject Applications",
  viewAuditLog: "View Audit Log",
  manageRoles: "Manage Roles",
  exportData: "Export Data",
  systemSettings: "System Settings",
};

function StatusPill({ status, tone }) {
  const base = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium";
  if (tone === "red") {
    return (
      <span className={`${base} bg-red-100 text-red-700`}>
        <XCircle className="h-4 w-4" />
        {status}
      </span>
    );
  }
  return (
    <span className={`${base} bg-sky-100 text-sky-700`}>
      <CheckCircle2 className="h-4 w-4" />
      {status}
    </span>
  );
}

function Modal({ open, title, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto flex-1">{children}</div>
          <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <div className="relative">
        {Icon ? <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /> : null}
        <div className={Icon ? "pl-9" : ""}>{children}</div>
      </div>
    </label>
  );
}

function Input({ value, onChange, placeholder, type = "text", disabled = false }) {
  return (
    <input
      type={type}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

function Textarea({ value, onChange, placeholder, disabled = false, rows = 3 }) {
  return (
    <textarea
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

function Select({ value, onChange, options, disabled = false }) {
  return (
    <div className="relative">
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2 pr-9 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
    </div>
  );
}

function makeId(prefix = "USR") {
  const n = Math.floor(100000 + Math.random() * 900000);
  return `${prefix}-${String(n).padStart(3, "0")}`;
}

function slugifyUsername(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 24);
}

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  let out = "";
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function UserRoleManagementPage() {
  const [users, setUsers] = React.useState(initialUsers);
  const [auditLog, setAuditLog] = React.useState(initialAuditLog);
  const [loading, setLoading] = React.useState(false); // For future API integration
  const [q, setQ] = React.useState("");
  const [roleFilter, setRoleFilter] = React.useState("All");
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [modal, setModal] = React.useState({ open: false, mode: "add", user: null });
  const [draft, setDraft] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [flashPassword, setFlashPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [activeRoleTab, setActiveRoleTab] = React.useState("Admin");

  const filtered = React.useMemo(() => {
    const ql = q.trim().toLowerCase();
    return users.filter((u) => {
      const matchesQ =
        !ql ||
        u.id.toLowerCase().includes(ql) ||
        u.name.toLowerCase().includes(ql) ||
        (u.email || "").toLowerCase().includes(ql) ||
        (u.username || "").toLowerCase().includes(ql);

      const matchesRole = roleFilter === "All" || u.role === roleFilter;
      const matchesStatus = statusFilter === "All" || u.status === statusFilter;

      return matchesQ && matchesRole && matchesStatus;
    });
  }, [users, q, roleFilter, statusFilter]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const addAuditLog = (action, details) => {
    const newLog = {
      id: `LOG-${auditLog.length + 1}`.padStart(7, "0"),
      user: "Current Admin",
      userId: "USR-001",
      action,
      details,
      timestamp: new Date().toISOString().replace("T", " ").substring(0, 19),
      ipAddress: "192.168.1.100",
    };
    setAuditLog([newLog, ...auditLog]);
  };

  const openAdd = () => {
    const pwd = genPassword();
    const base = {
      id: makeId("USR"),
      name: "",
      email: "",
      phone: "",
      username: "",
      password: pwd,
      role: "Staff",
      status: "Active",
      tone: "blue",
      createdDate: new Date().toISOString().split("T")[0],
      lastLogin: "",
      createdBy: "Current Admin",
      department: "Operations",
      notes: "",
    };
    setFlashPassword(pwd);
    setDraft(base);
    setModal({ open: true, mode: "add", user: null });
  };

  const openEdit = (u) => {
    setFlashPassword("");
    setDraft({ ...u, password: "" });
    setModal({ open: true, mode: "edit", user: u });
  };

  const openView = (u) => {
    setFlashPassword("");
    setDraft({ ...u, password: "" });
    setModal({ open: true, mode: "view", user: u });
  };

  const closeModal = () => {
    setModal({ open: false, mode: "add", user: null });
    setDraft(null);
    setFlashPassword("");
    setShowPassword(false);
  };

  const saveAdd = () => {
    if (!draft?.name?.trim()) {
      showNotification("Name is required", "error");
      return;
    }
    if (!draft?.email?.trim()) {
      showNotification("Email is required", "error");
      return;
    }
    if (!draft?.username?.trim()) {
      showNotification("Username is required", "error");
      return;
    }
    if (!draft?.password?.trim()) {
      showNotification("Password is required", "error");
      return;
    }

    setUsers([{ ...draft }, ...users]);
    addAuditLog("Added User", `Added new user: ${draft.name} (${draft.role})`);
    showNotification("User added successfully!");
    closeModal();
  };

  const saveEdit = () => {
    if (!draft?.name?.trim()) {
      showNotification("Name is required", "error");
      return;
    }
    if (!draft?.email?.trim()) {
      showNotification("Email is required", "error");
      return;
    }
    if (!draft?.username?.trim()) {
      showNotification("Username is required", "error");
      return;
    }

    setUsers(users.map((u) => (u.id === draft.id ? { ...draft, password: undefined } : u)));
    addAuditLog("Updated User", `Updated user details: ${draft.name}`);
    showNotification("User updated successfully!");
    closeModal();
  };

  const toggleSuspension = (user) => {
    const newStatus = user.status === "Active" ? "Suspended" : "Active";
    const newTone = newStatus === "Active" ? "blue" : "red";
    const action = newStatus === "Suspended" ? "suspend" : "activate";

    if (window.confirm(`Are you sure you want to ${action} ${user.name}?`)) {
      setUsers(users.map((u) => (u.id === user.id ? { ...u, status: newStatus, tone: newTone } : u)));
      addAuditLog(
        newStatus === "Suspended" ? "Suspended User" : "Activated User",
        `${newStatus === "Suspended" ? "Suspended" : "Activated"} ${user.name} account`
      );
      showNotification(`${user.name} has been ${action}ed successfully!`);
    }
  };

  const removeUser = (user) => {
    if (window.confirm(`Are you sure you want to remove ${user.name}? This action cannot be undone.`)) {
      setUsers(users.filter((u) => u.id !== user.id));
      addAuditLog("Removed User", `Removed user: ${user.name} (${user.email})`);
      showNotification(`${user.name} has been removed successfully!`);
    }
  };

  const resetPassword = () => {
    const pwd = genPassword();
    setDraft((d) => ({ ...d, password: pwd }));
    setFlashPassword(pwd);
    showNotification("New password generated successfully!");
  };

  const handleExport = () => {
    const csv = [
      ["User ID", "Name", "Email", "Username", "Role", "Status", "Department", "Created Date"],
      ...filtered.map((u) => [u.id, u.name, u.email, u.username, u.role, u.status, u.department, u.createdDate]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_export.csv";
    a.click();
    showNotification("Users exported successfully!");
  };

  const handleExportAuditLog = () => {
    const csv = [
      ["Log ID", "User", "Action", "Details", "Timestamp", "IP Address"],
      ...auditLog.map((log) => [log.id, log.user, log.action, log.details, log.timestamp, log.ipAddress]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "audit_log_export.csv";
    a.click();
    showNotification("Audit log exported successfully!");
  };

  return (
    <div className="space-y-4">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl border shadow-lg flex items-center gap-2 animate-slide-in ${
            notification.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {notification.type === "success" ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span className="text-xs font-medium">{notification.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-2xl bg-sky-50 flex items-center justify-center">
            <Shield className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-sm md:text-base font-semibold text-slate-900">User & Role Management</h1>
            <p className="text-[11px] text-slate-500">Manage users, roles, and permissions</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sky-500 text-white text-xs font-medium hover:bg-sky-600"
          >
            <UserPlus className="h-4 w-4" />
            Add User
          </button>

          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50"
            title="Export to CSV"
          >
            <Download className="h-4 w-4" />
            Export
          </button>

          <div className="hidden md:flex items-center px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="text"
              placeholder="Search users..."
              className="bg-transparent text-xs outline-none w-40"
            />
            {q ? (
              <button onClick={() => setQ("")} className="text-slate-400 hover:text-slate-700" title="Clear">
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Mobile search & filters */}
      <div className="md:hidden bg-white rounded-2xl border border-slate-200 px-3 py-2 space-y-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            type="text"
            placeholder="Search users..."
            className="bg-transparent text-xs outline-none w-full"
          />
          {q ? (
            <button onClick={() => setQ("")} className="text-slate-400 hover:text-slate-700" title="Clear">
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-slate-500" />
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 bg-white">
          <span className="text-slate-500">Role:</span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-transparent outline-none text-xs"
          >
            <option>All</option>
            {roles.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 bg-white">
          <span className="text-slate-500">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-transparent outline-none text-xs"
          >
            <option>All</option>
            <option>Active</option>
            <option>Suspended</option>
          </select>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">User ID</th>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Role</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableSkeleton rows={8} cols={6} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-xs text-slate-500">
                  No users found.
                </td>
              </tr>
            ) : (
              filtered.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/40">
                  <td className="px-4 py-3 text-slate-900">{u.id}</td>
                  <td className="px-4 py-3 text-slate-800">{u.name}</td>
                  <td className="px-4 py-3 text-slate-800">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        u.role === "Admin"
                          ? "bg-purple-100 text-purple-700"
                          : u.role === "Manager"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={u.status} tone={u.tone} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 text-slate-700">
                      <button className="hover:text-sky-500" onClick={() => openEdit(u)} title="Edit user">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button className="hover:text-sky-500" onClick={() => openView(u)} title="View user">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        className={u.status === "Active" ? "hover:text-orange-500" : "hover:text-green-500"}
                        onClick={() => toggleSuspension(u)}
                        title={u.status === "Active" ? "Suspend user" : "Activate user"}
                      >
                        {u.status === "Active" ? <XCircle className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      </button>
                      <button className="hover:text-red-500" onClick={() => removeUser(u)} title="Remove user">
                        <UserMinus className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Roles & Permissions */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="h-4 w-4 text-slate-500" />
            Roles & Permissions
          </div>
        </div>

        {/* Role Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
          {roles.map((role) => (
            <button
              key={role}
              onClick={() => setActiveRoleTab(role)}
              className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors ${
                activeRoleTab === role
                  ? role === "Admin"
                    ? "bg-purple-500 text-white"
                    : role === "Manager"
                    ? "bg-blue-500 text-white"
                    : "bg-slate-700 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {role}
            </button>
          ))}
        </div>

        {/* Permissions Matrix */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 font-medium text-xs text-slate-700">
            {activeRoleTab} Permissions
          </div>
          <div className="overflow-x-auto">
            {Object.entries(permissionCategories).map(([category, permissions]) => (
              <div key={category}>
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-900">
                  {category}
                </div>
                {permissions.map((perm) => (
                  <div
                    key={perm}
                    className="flex items-center justify-between px-4 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50/50"
                  >
                    <span className="text-xs text-slate-700">{permissionLabels[perm]}</span>
                    <div className="flex items-center gap-4">
                      {rolePermissions[activeRoleTab][perm] ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-slate-300" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            Audit Log
          </div>
          <button
            onClick={handleExportAuditLog}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            Export Log
          </button>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left font-medium">User</th>
                <th className="px-4 py-2 text-left font-medium">Action</th>
                <th className="px-4 py-2 text-left font-medium">Details</th>
                <th className="px-4 py-2 text-left font-medium">Date / Time</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.slice(0, 10).map((log, idx) => (
                <tr
                  key={log.id}
                  className={`border-b border-slate-100 last:border-0 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}
                >
                  <td className="px-4 py-2 text-slate-800">{log.user}</td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-[11px] font-medium">
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{log.details}</td>
                  <td className="px-4 py-2 text-slate-600">{log.timestamp}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditLog.length > 10 && (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-600 text-center">
              Showing 10 of {auditLog.length} entries
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal
        open={modal.open}
        title={
          modal.mode === "add" ? "Add New User" : modal.mode === "edit" ? "Edit User" : "View User Details"
        }
        onClose={closeModal}
        footer={
          <div className="w-full flex items-center justify-between">
            <button
              onClick={closeModal}
              className="px-3 py-2 rounded-xl text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>

            {modal.mode === "add" ? (
              <button
                onClick={saveAdd}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-sky-500 text-white hover:bg-sky-600"
              >
                Create User
              </button>
            ) : modal.mode === "edit" ? (
              <button
                onClick={saveEdit}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-sky-500 text-white hover:bg-sky-600"
              >
                Save Changes
              </button>
            ) : null}
          </div>
        }
      >
        {!draft ? null : (
          <div className="space-y-4">
            {/* Flash password notification */}
            {flashPassword && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <div className="font-semibold mb-1">Password Generated</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="px-2 py-1 rounded-xl border border-amber-200 bg-white text-[11px]">
                    {flashPassword}
                  </code>
                  <button
                    onClick={() => navigator.clipboard?.writeText(flashPassword)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
                    type="button"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </button>
                </div>
                <div className="mt-1 text-[11px] text-amber-700">
                  Share this password securely. Store only hashed passwords server-side.
                </div>
              </div>
            )}

            {/* User Information */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                User Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {modal.mode !== "add" && (
                  <Field label="User ID">
                    <Input value={draft.id} onChange={() => {}} disabled />
                  </Field>
                )}

                <Field label="Full Name *" icon={User}>
                  <Input
                    value={draft.name}
                    onChange={(v) => {
                      setDraft((d) => ({
                        ...d,
                        name: v,
                        username: d.username ? d.username : slugifyUsername(v),
                      }));
                    }}
                    placeholder="e.g. John Doe"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Email *" icon={Mail}>
                  <Input
                    type="email"
                    value={draft.email}
                    onChange={(v) => setDraft((d) => ({ ...d, email: v }))}
                    placeholder="john.doe@example.com"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Phone" icon={Phone}>
                  <Input
                    value={draft.phone || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, phone: v }))}
                    placeholder="+44 20 1234 5678"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Role *" icon={Shield}>
                  <Select
                    value={draft.role}
                    onChange={(v) => setDraft((d) => ({ ...d, role: v }))}
                    disabled={modal.mode === "view"}
                    options={roles.map((r) => ({ label: r, value: r }))}
                  />
                </Field>

                <Field label="Department" icon={User}>
                  <Select
                    value={draft.department || "Operations"}
                    onChange={(v) => setDraft((d) => ({ ...d, department: v }))}
                    disabled={modal.mode === "view"}
                    options={departments.map((d) => ({ label: d, value: d }))}
                  />
                </Field>

                <Field label="Status" icon={CheckCircle2}>
                  <Select
                    value={draft.status}
                    onChange={(v) => setDraft((d) => ({ ...d, status: v, tone: v === "Active" ? "blue" : "red" }))}
                    disabled={modal.mode === "view"}
                    options={[
                      { label: "Active", value: "Active" },
                      { label: "Suspended", value: "Suspended" },
                    ]}
                  />
                </Field>

                {modal.mode !== "add" && (
                  <>
                    <Field label="Created Date" icon={Calendar}>
                      <Input value={draft.createdDate} onChange={() => {}} disabled />
                    </Field>

                    <Field label="Created By" icon={User}>
                      <Input value={draft.createdBy} onChange={() => {}} disabled />
                    </Field>

                    <Field label="Last Login" icon={Calendar}>
                      <Input value={draft.lastLogin || "Never"} onChange={() => {}} disabled />
                    </Field>
                  </>
                )}
              </div>
            </div>

            {/* Login Credentials */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4 text-slate-500" />
                Login Credentials
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Username *" icon={User}>
                  <Input
                    value={draft.username || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, username: slugifyUsername(v) }))}
                    placeholder="e.g. johndoe"
                    disabled={modal.mode === "view"}
                  />
                  {modal.mode !== "view" && (
                    <div className="mt-1 text-xs text-slate-500">Auto-generated from name if left empty</div>
                  )}
                </Field>

                {modal.mode !== "view" && (
                  <div className="flex items-end">
                    <button
                      onClick={resetPassword}
                      type="button"
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <RefreshCw className="h-4 w-4 text-slate-500" />
                      {modal.mode === "add" ? "Regenerate Password" : "Reset Password"}
                    </button>
                  </div>
                )}
              </div>

              {modal.mode !== "view" && draft.password && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Password (generated)" icon={Lock}>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={draft.password || ""}
                        onChange={(v) => setDraft((d) => ({ ...d, password: v }))}
                        placeholder="Generated password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </Field>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(draft.password || "")}
                      disabled={!draft.password}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-sky-500 text-white text-xs font-medium hover:bg-sky-600 disabled:opacity-50"
                    >
                      <Copy className="h-4 w-4" />
                      Copy Password
                    </button>
                  </div>
                </div>
              )}

              {modal.mode === "view" && (
                <div className="mt-2 text-xs text-slate-500">Password is hidden for security.</div>
              )}
            </div>

            {/* Permissions Preview */}
            {draft.role && (
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-500" />
                  {draft.role} Permissions
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(rolePermissions[draft.role])
                    .filter(([, value]) => value)
                    .map(([key]) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700"
                      >
                        <Check className="h-3 w-3" />
                        {permissionLabels[key]}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-500" />
                Notes
              </div>
              <Field label="Additional notes">
                <Textarea
                  value={draft.notes || ""}
                  onChange={(v) => setDraft((d) => ({ ...d, notes: v }))}
                  placeholder="Add any additional notes about this user..."
                  disabled={modal.mode === "view"}
                  rows={3}
                />
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}